'use client'

import { useEffect, useState } from 'react'
import { BiMoon, BiSun } from 'react-icons/bi'

import './styles.scss'

const STORAGE_KEY = 'sf@theme'

function getEffectiveDark(): boolean {
  if (typeof document === 'undefined') return false
  const stored = getStoredPreference()
  if (stored !== null) return stored === 'dark'
  const theme = document.documentElement.getAttribute('data-theme')
  if (theme === 'dark' || theme === 'light') return theme === 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function getStoredPreference(): 'light' | 'dark' | null {
  if (typeof localStorage === 'undefined') return null
  const v = localStorage.getItem(STORAGE_KEY)
  if (v === 'light' || v === 'dark') return v
  return null
}

function applyTheme(dark: boolean) {
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
  localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light')
}

export function ThemeToggle() {
  const [isDark, setIsDark] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const effectiveDark = getEffectiveDark()
    setIsDark(effectiveDark)
    applyTheme(effectiveDark)
    setMounted(true)
  }, [])

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', () => {
      console.log('mq.matches', mq.matches)
      applyTheme(mq.matches)
      setIsDark(mq.matches)
    })
    return () => mq.removeEventListener('change', () => {})
  }, [])

  const handleToggle = () => {
    const stored = getStoredPreference()
    const prefersDark =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    const nextDark = stored !== null ? !getEffectiveDark() : !prefersDark
    applyTheme(nextDark)
    setIsDark(nextDark)
  }

  if (!mounted) {
    return (
      <span
        className="theme-toggle theme-toggle--placeholder"
        aria-hidden
        style={{ width: 48, height: 48, display: 'inline-block' }}
      />
    )
  }

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={handleToggle}
      aria-label={isDark ? 'Usar tema claro' : 'Usar tema escuro'}
      title={isDark ? 'Tema claro' : 'Tema escuro'}
    >
      {isDark ? (
        <BiSun
          size={28}
          className="theme-toggle__icon theme-toggle__icon--sun"
          aria-hidden
        />
      ) : (
        <BiMoon
          size={28}
          className="theme-toggle__icon theme-toggle__icon--moon"
          aria-hidden
        />
      )}
    </button>
  )
}
